import { Link, Navigate } from "react-router"
import { useTranslation } from "react-i18next"

import { PermissionDenied } from "@/components/kit/PermissionDenied"
import { PageHeader } from "@/components/kit/PageHeader"
import { RangePicker } from "@/components/kit/RangePicker"
import { ErrorState, LoadingState } from "@/components/kit/states"
import { Button } from "@/components/ui/button"
import { useMe } from "@/auth/session"
import { localeOf } from "@/lib/format"
import {
  useAnyKey,
  useAnyMessage,
  useFirstDevices,
  useOverview,
  useOverviewAccess,
  useOverviewRange,
  useTraffic,
  useTrafficWindow,
} from "../api/overview"
import { checklist, landsOnAdmin } from "../lib/overview"
import { ActionCards } from "../components/ActionCards"
import { Figures } from "../components/Figures"
import { GetStarted } from "../components/GetStarted"
import { Region } from "../components/Region"
import { StatusBreakdown } from "../components/StatusBreakdown"
import { TopDevices } from "../components/TopDevices"
import { TrafficChart } from "../components/TrafficChart"
import { UsageCard } from "../components/UsageCard"

/**
 * `/` (LLD §9.3): what needs doing, the plan's usage, the range's figures,
 * delivery and the busiest phones. Every region asks only what the caller
 * may read and fails on its own; the checklist is the onboarding, so the
 * page has no empty state of its own.
 */
export default function OverviewScreen() {
  const { t, i18n } = useTranslation(["overview", "common"])
  const locale = localeOf(i18n.language)
  const { data: me } = useMe()
  const can = useOverviewAccess()
  const [range, setRange] = useOverviewRange()
  const overview = useOverview(range, can.figures)
  const devices = useFirstDevices(can.devices)
  const keys = useAnyKey(can.keys)
  const messages = useAnyMessage(can.messages)
  const window = useTrafficWindow(range)
  const traffic = useTraffic(window, can.messages)

  if (me && landsOnAdmin(me.permissions)) return <Navigate to="/admin" replace />

  const count = (on: boolean, n: number | undefined) => (on ? n : undefined)
  const list = checklist({
    devices: count(can.devices, devices.data?.items.length),
    keys: count(can.keys, keys.data?.items.length),
    messages: count(can.messages, messages.data?.items.length),
  })
  // Hold the checklist until every count it needs has answered: it would
  // otherwise flash "to do" at someone who finished long ago.
  const countsReady =
    (!can.devices || devices.data) && (!can.keys || keys.data) && (!can.messages || messages.data)

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={me ? t("greeting", { name: me.user.name }) : t("description")}
      />
      {countsReady ? <GetStarted list={list} /> : null}
      <ActionCards overview={overview.data} />

      <div className="grid gap-6 xl:grid-cols-3">
        <section aria-labelledby="overview-figures" className="min-w-0 space-y-3 xl:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="overview-figures" className="text-base font-semibold">
              {t("figures.title")}
            </h2>
            {can.figures ? <RangePicker value={range} onChange={setRange} locale={locale} /> : null}
          </div>
          {!can.figures ? (
            <PermissionDenied required={["account.profile:read"]} />
          ) : overview.error ? (
            <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
          ) : overview.data ? (
            <Figures figures={overview.data.messages} />
          ) : (
            <LoadingState variant="detail" rows={2} />
          )}
          {can.messages ? (
            <Region title={t("traffic.title")} query={traffic.current}>
              {() =>
                traffic.days ? (
                  <TrafficChart days={traffic.days} locale={locale} {...window} />
                ) : null
              }
            </Region>
          ) : null}
        </section>
        <UsageCard className="min-w-0" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {can.figures ? (
          <Region title={t("breakdown.title")} query={overview}>
            {() => (overview.data ? <StatusBreakdown figures={overview.data.messages} /> : null)}
          </Region>
        ) : null}
        {can.devices ? (
          <Region
            title={t("devices.title")}
            query={devices}
            skeleton="table"
            action={
              <Button asChild size="sm" variant="ghost">
                <Link to="/devices">{t("devices.all")}</Link>
              </Button>
            }
          >
            {() => <TopDevices items={devices.data?.items ?? []} />}
          </Region>
        ) : null}
      </div>
    </div>
  )
}
